import { Stack, useTheme } from '@mui/material';
import type { ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';

import { useChargeView } from '@woovi/hooks';
import { CHARGE_STATUS_ENUM, PAYMENT_METHOD_TYPE } from '@woovi/shared';
import { PaymentFooter } from '@woovi/ui';

import type { PaymentCharge_charge$key } from './__generated__/PaymentCharge_charge.graphql.ts';
import type { PaymentCharge_query$key } from './__generated__/PaymentCharge_query.graphql.ts';
import PaymentDispute from './PaymentDispute.tsx';
import PaymentShopify from './PaymentShopify.tsx';
import PaymentActive from '../active/PaymentActive.tsx';
import PaymentMethodCreditCardSelected from '../activeWaitingPaymentMethod/creditCard/PaymentMethodCreditCardSelected.tsx';
import PaymentActiveWaitingPaymentMethodRoot from '../activeWaitingPaymentMethod/PaymentActiveWaitingPaymentMethodRoot.tsx';
import PaymentMethodBoletoSelected from '../boleto/PaymentMethodBoletoSelected.tsx';
import PaymentCompleted from '../completed/PaymentCompleted.tsx';
import PaymentActiveWaitingCustomerFillData from '../customerRegister/PaymentActiveWaitingCustomerFillData.tsx';
import PaymentDeleted from '../deleted/PaymentDeleted.tsx';
import PaymentExpired from '../expired/PaymentExpired.tsx';
import PaymentCompanyLogo from '../logo/PaymentCompanyLogo.tsx';
import PaymentMethodSelectedPending from '../PaymentMethodSelectedPending.tsx';
import PaymentTestAccountBanner from '../PaymentTestAccountBanner.tsx';
import PaymentMethodPixSelected from '../pix/PaymentMethodPixSelected.tsx';
import PaymentCompanySupport from '../support/PaymentCompanySupport.tsx';

type PaymentChargeProps = {
  query: PaymentCharge_query$key;
  charge: PaymentCharge_charge$key;
  alwaysShowQrCode?: boolean;
  allowSimulatePayment?: boolean;
};

const PaymentCharge = (props: PaymentChargeProps): ReactNode => {
  const theme = useTheme();
  const query = useFragment<PaymentCharge_query$key>(
    graphql`
      fragment PaymentCharge_query on Query {
        ...PaymentActiveWaitingPaymentMethodRoot_query
        ...PaymentActive_query
        ...PaymentCompleted_query
        ...PaymentMethodCreditCardSelected_query
        ...PaymentMethodBoletoSelected_query
      }
    `,
    props.query,
  );

  const charge = useFragment<PaymentCharge_charge$key>(
    graphql`
      fragment PaymentCharge_charge on Charge {
        id
        status
        isRemoved
        customerRegister
        customer {
          name
        }
        isFromTestAccount
        paymentMethodSelected
        company {
          projectType
          ...PaymentCompanySupport_company
          ...PaymentCompanyLogo_company
        }
        ...PaymentActive_charge
        ...PaymentActiveWaitingPaymentMethodRoot_charge
        ...PaymentDeleted_charge
        ...PaymentCompleted_charge
        ...PaymentExpired_charge
        ...PaymentActiveWaitingCustomerFillData_charge
        ...PaymentTestAccountBanner_charge
        ...PaymentMethodCreditCardSelected_charge
        ...PaymentMethodBoletoSelected_charge
        ...PaymentDispute_charge
        ...PaymentMethodSelectedPending_charge
        ...PaymentMethodPixSelected_charge
        ...PaymentShopify_charge
      }
    `,
    props.charge,
  );

  useChargeView(charge?.id);

  const renderChargeByStatus = (): ReactNode => {
    const isChargeActiveWaitingPaymentMethod =
      charge.status === CHARGE_STATUS_ENUM.ACTIVE_WAITING_PAYMENT_METHOD;

    const isChargeActive = charge.status === CHARGE_STATUS_ENUM.ACTIVE;
    const isChargeOverdue = charge.status === CHARGE_STATUS_ENUM.OVERDUE;
    const isChargeCompleted = charge.status === CHARGE_STATUS_ENUM.COMPLETED;
    const isChargeExpired = charge.status === CHARGE_STATUS_ENUM.EXPIRED;

    const hasCustomer = charge?.customer?.name;

    if (isChargeCompleted) {
      return <PaymentCompleted query={query} charge={charge} />;
    }

    if (charge.isRemoved) {
      return <PaymentDeleted charge={charge} pixQrCode={null} />;
    }

    if (isChargeExpired) {
      return <PaymentExpired charge={charge} />;
    }

    // TODO: Use enum instead
    if (charge.paymentMethodSelected === 'PENDING') {
      return <PaymentMethodSelectedPending charge={charge} />;
    }

    if (charge.paymentMethodSelected === PAYMENT_METHOD_TYPE.CREDIT_CARD) {
      return <PaymentMethodCreditCardSelected charge={charge} query={query} />;
    }

    if (charge.paymentMethodSelected === PAYMENT_METHOD_TYPE.BOLETO) {
      return <PaymentMethodBoletoSelected charge={charge} query={query} />;
    }

    if (charge.paymentMethodSelected === PAYMENT_METHOD_TYPE.PIX) {
      return <PaymentMethodPixSelected charge={charge} />;
    }

    if (charge.customerRegister && !hasCustomer) {
      return <PaymentActiveWaitingCustomerFillData charge={charge} />;
    }

    if (isChargeActiveWaitingPaymentMethod) {
      return (
        <PaymentActiveWaitingPaymentMethodRoot query={query} charge={charge} />
      );
    }

    if (isChargeActive || isChargeOverdue) {
      return (
        <PaymentActive
          allowSimulatePayment={props.allowSimulatePayment}
          alwaysShowQrCode={props.alwaysShowQrCode}
          charge={charge}
          query={query}
        />
      );
    }
  };

  return (
    <Stack spacing={theme.spacing(2)}>
      <PaymentTestAccountBanner charge={charge} pixQrCode={null} />
      <PaymentCompanyLogo company={charge.company} />
      {renderChargeByStatus()}
      <PaymentCompanySupport company={charge.company} />
      <PaymentFooter projectType={charge.company?.projectType} />
      <PaymentShopify charge={charge} />
      <PaymentDispute charge={charge} />
    </Stack>
  );
};

export default PaymentCharge;
